import {
  ComponentIds,
  Entity,
  HierarchyUtils,
  SnapRootComponent,
  TransformUtils,
  Vector
} from '@inter-ikea-core/core';
import { CoreApi, Product } from '@inter-ikea-storageone/range';
import { EkrisComponent } from '@inter-ikea-storageone/range-eket';

export const getRandomBlock = (frameProducts: Product[], coreApi: CoreApi) => {
  const productsToUse = frameProducts
    .filter(
      p =>
        p.communicatedDimensions.width === 350 &&
        p.communicatedDimensions.height === 350 &&
        p.communicatedDimensions.depth === 350
    )
    .sort(
      (a: Product, b: Product) => a.priceInformation.regularPrice - b.priceInformation.regularPrice
    )
    .filter((product, index, self) => index === self.findIndex(p => p.design === product.design));
  const random = Math.floor(Math.random() * 5);
  switch (random) {
    case 0:
      return createLBlock(productsToUse[0], coreApi);
    case 1:
      return createIBlock(productsToUse[1], coreApi);
    case 2:
      return createSquareBlock(productsToUse[2], coreApi);
    case 3:
      return createSquigglyBlock(productsToUse[3], coreApi);
    case 4:
      return createTBlock(productsToUse[4], coreApi);
    default:
      return createLBlock(productsToUse[5], coreApi);
  }
};

const createLBlock = (product: Product, coreApi: CoreApi) => {
  const xMultiplier = Math.random() < 0.5 ? 1 : -1;
  const newBlock = createEntity(coreApi);
  newBlock.addComponent(new SnapRootComponent());
  addSubBlock(newBlock, 0, 0, product.coreProductId, coreApi);
  addSubBlock(newBlock, 0, -1, product.coreProductId, coreApi);
  addSubBlock(newBlock, 0, -2, product.coreProductId, coreApi);
  addSubBlock(newBlock, 1 * xMultiplier, -2, product.coreProductId, coreApi);
  HierarchyUtils.setParent(newBlock, coreApi.getServices().sceneManager.scene);
  return newBlock;
};

const createSquigglyBlock = (product: Product, coreApi: CoreApi) => {
  const xMultiplier = Math.random() < 0.5 ? 1 : -1;
  const yMultiplier = Math.random() < 0.5 ? 1 : -1;
  const newBlock = createEntity(coreApi);
  newBlock.addComponent(new SnapRootComponent());
  addSubBlock(newBlock, 0, 0, product.coreProductId, coreApi);
  addSubBlock(newBlock, 1 * xMultiplier, 0, product.coreProductId, coreApi);
  addSubBlock(newBlock, 0, -1 * yMultiplier, product.coreProductId, coreApi);
  addSubBlock(newBlock, -1 * xMultiplier, -1 * yMultiplier, product.coreProductId, coreApi);
  HierarchyUtils.setParent(newBlock, coreApi.getServices().sceneManager.scene);
  return newBlock;
};

const createIBlock = (product: Product, coreApi: CoreApi) => {
  const newBlock = createEntity(coreApi);
  newBlock.addComponent(new SnapRootComponent());
  addSubBlock(newBlock, 0, 0, product.coreProductId, coreApi);
  addSubBlock(newBlock, 0, -1, product.coreProductId, coreApi);
  addSubBlock(newBlock, 0, -2, product.coreProductId, coreApi);
  addSubBlock(newBlock, 0, -3, product.coreProductId, coreApi);
  HierarchyUtils.setParent(newBlock, coreApi.getServices().sceneManager.scene);
  return newBlock;
};

const createSquareBlock = (product: Product, coreApi: CoreApi) => {
  const newBlock = createEntity(coreApi);
  newBlock.addComponent(new SnapRootComponent());
  addSubBlock(newBlock, 0, 0, product.coreProductId, coreApi);
  addSubBlock(newBlock, 0, -1, product.coreProductId, coreApi);
  addSubBlock(newBlock, 1, -1, product.coreProductId, coreApi);
  addSubBlock(newBlock, 1, 0, product.coreProductId, coreApi);
  HierarchyUtils.setParent(newBlock, coreApi.getServices().sceneManager.scene);
  return newBlock;
};

const createTBlock = (product: Product, coreApi: CoreApi) => {
  const newBlock = createEntity(coreApi);
  newBlock.addComponent(new SnapRootComponent());
  addSubBlock(newBlock, 0, 0, product.coreProductId, coreApi);
  addSubBlock(newBlock, -1, 0, product.coreProductId, coreApi);
  addSubBlock(newBlock, 1, 0, product.coreProductId, coreApi);
  addSubBlock(newBlock, 0, -1, product.coreProductId, coreApi);
  HierarchyUtils.setParent(newBlock, coreApi.getServices().sceneManager.scene);
  return newBlock;
};

const addSubBlock = (parent: Entity, x: number, y: number, productId: string, coreApi: CoreApi) => {
  const block = createEntity(coreApi, productId)!;
  HierarchyUtils.setParent(block, parent);
  block.removeComponent(ComponentIds.SNAP_ROOT_COMPONENT_ID);
  TransformUtils.setLocalPosition(block, new Vector(x * 350, y * 350, 0));
};

const createEntity = (coreApi: CoreApi, productId?: string) => {
  const entity = productId
    ? coreApi.getServices().productManager.createEntity(productId)!
    : coreApi.getServices().entityManager.createEntity();
  entity.addComponent(new EkrisComponent());
  return entity;
};
